import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { OffsetDateTime, LocalDate, LocalTime } from "./branded-types";
import { VANCOUVER_TZ } from "./constants";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

// ============ Internal helpers (not exported) ============

function parseTimestamp(value: OffsetDateTime): dayjs.Dayjs {
  return dayjs.utc(value).tz(VANCOUVER_TZ);
}

function parseDate(value: LocalDate): dayjs.Dayjs {
  return dayjs.tz(value, VANCOUVER_TZ).startOf("day");
}

// ============ Type guards (runtime checks for branded types) ============

// ISO 8601 timestamp pattern: 2024-01-15T10:30:00.000Z or 2024-01-15T10:30:00Z
const ISO_TIMESTAMP_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;

// ISO date pattern: 2024-01-15
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// Time-of-day pattern: HH:MM or HH:MM:SS
const LOCAL_TIME_REGEX = /^\d{2}:\d{2}(:\d{2})?$/;

/**
 * Runtime type guard to check if a value is an OffsetDateTime.
 * Matches ISO 8601 UTC timestamps like "2024-01-15T10:30:00.000Z"
 */
export function isOffsetDateTime(value: unknown): value is OffsetDateTime {
  return typeof value === "string" && ISO_TIMESTAMP_REGEX.test(value);
}

/**
 * Runtime type guard to check if a value is a LocalDate.
 * Matches date strings like "2024-01-15"
 */
export function isLocalDate(value: unknown): value is LocalDate {
  return typeof value === "string" && ISO_DATE_REGEX.test(value);
}

/**
 * Runtime type guard to check if a value is a LocalTime.
 * Matches time-of-day strings like "09:30" or "09:30:00"
 */
export function isLocalTime(value: unknown): value is LocalTime {
  return typeof value === "string" && LOCAL_TIME_REGEX.test(value);
}

// ============ Current time (matches backend naming) ============

/**
 * Returns the current UTC timestamp as an OffsetDateTime.
 * Use this instead of `new Date().toISOString()` or `dayjs().toISOString()`.
 */
export function now(): OffsetDateTime {
  return dayjs.utc().toISOString() as OffsetDateTime;
}

/**
 * Returns an OffsetDateTime `hours` hours before now (UTC). Negative values
 * project into the future. Useful for building time-window query parameters,
 * e.g. `closedSince: hoursAgo(24)`.
 */
export function hoursAgo(hours: number): OffsetDateTime {
  return dayjs.utc().subtract(hours, "hour").toISOString() as OffsetDateTime;
}

/**
 * Returns today's date in Vancouver timezone as a LocalDate.
 * Use this instead of `dayjs().format("YYYY-MM-DD")`.
 */
export function today(): LocalDate {
  return dayjs().tz(VANCOUVER_TZ).format("YYYY-MM-DD") as LocalDate;
}

/**
 * Returns the current year in Vancouver timezone.
 * Use this instead of `new Date().getFullYear()`.
 */
export function getCurrentYear(): number {
  return dayjs().tz(VANCOUVER_TZ).year();
}

// ============ Formatting ============

/** Default format for timestamps: "Wed, Jan 15, 2024 at 3:30 PM PST" */
const DEFAULT_TIMESTAMP_FORMAT = "ddd, MMM D, YYYY [at] h:mm A z";

/** Default format for dates: "Wed January 15th" */
const DEFAULT_DATE_FORMAT = "ddd MMMM Do";

/**
 * Format a UTC timestamp for display in Vancouver timezone.
 * @param value - The UTC timestamp to format
 * @param format - The dayjs format string (default: "ddd, MMM D, YYYY [at] h:mm A z")
 * @param fallback - Value to return if value is null/undefined
 */
export function formatOffsetDateTime(
  value: OffsetDateTime | undefined | null,
  format: string = DEFAULT_TIMESTAMP_FORMAT,
  fallback = "",
): string {
  if (!value) return fallback;
  return parseTimestamp(value).format(format);
}

/**
 * Format a date for display.
 * @param value - The date string to format
 * @param format - The dayjs format string (default: "ddd MMMM Do")
 * @param fallback - Value to return if value is null/undefined
 */
export function formatLocalDate(
  value: LocalDate | undefined | null,
  format: string = DEFAULT_DATE_FORMAT,
  fallback = "",
): string {
  if (!value) return fallback;
  return parseDate(value).format(format);
}

// ============ Relative time ============

/**
 * Returns a human-readable relative time string (e.g., "2 hours ago", "in 3 days").
 * @param value - The UTC timestamp
 * @param fallback - Value to return if value is null/undefined
 * @param referenceTime - Optional reference time to compare against (defaults to now)
 */
export function formatRelativeOffsetDateTime(
  value: OffsetDateTime | undefined | null,
  fallback = "",
  referenceTime?: OffsetDateTime,
): string {
  if (!value) return fallback;
  const parsed = parseTimestamp(value);
  if (referenceTime) {
    return parsed.from(parseTimestamp(referenceTime));
  }
  return parsed.fromNow();
}

// ============ Comparisons (all use branded types, no dayjs exposed) ============

/**
 * Returns true if the first timestamp is after the second.
 */
export function isOffsetDateTimeAfter(
  value: OffsetDateTime,
  other: OffsetDateTime,
): boolean {
  return parseTimestamp(value).isAfter(parseTimestamp(other));
}

/**
 * Returns true if the first timestamp is before the second.
 */
export function isOffsetDateTimeBefore(
  value: OffsetDateTime,
  other: OffsetDateTime,
): boolean {
  return parseTimestamp(value).isBefore(parseTimestamp(other));
}

/**
 * Returns true if the timestamp is within the last N units of time.
 * @param value - The UTC timestamp to check
 * @param amount - Number of units to look back
 * @param unit - The unit type: 'day', 'week', 'month', 'year', etc.
 * @example isWithinLast(timestamp, 30, 'days')
 * @example isWithinLast(timestamp, 2, 'weeks')
 */
export function isWithinLast(
  value: OffsetDateTime | undefined | null,
  amount: number,
  unit:
    | "day"
    | "days"
    | "week"
    | "weeks"
    | "month"
    | "months"
    | "year"
    | "years",
): boolean {
  if (!value) return false;
  const cutoff = dayjs().tz(VANCOUVER_TZ).subtract(amount, unit);
  return parseTimestamp(value).isAfter(cutoff);
}

/**
 * Returns true if the first date is after the second.
 */
export function isLocalDateAfter(value: LocalDate, other: LocalDate): boolean {
  return parseDate(value).isAfter(parseDate(other));
}

/**
 * Returns true if the first date is before the second.
 */
export function isLocalDateBefore(value: LocalDate, other: LocalDate): boolean {
  return parseDate(value).isBefore(parseDate(other));
}

/**
 * Returns true if the date is in the past (before today in Vancouver timezone).
 */
export function isLocalDateInThePast(value: LocalDate): boolean {
  return parseDate(value).isBefore(dayjs().tz(VANCOUVER_TZ).startOf("day"));
}

/**
 * Returns true if the date is in the future (after today in Vancouver timezone).
 */
export function isLocalDateInTheFuture(value: LocalDate): boolean {
  return parseDate(value).isAfter(dayjs().tz(VANCOUVER_TZ).startOf("day"));
}

// ============ Sorting ============
//
// ISO 8601 strings (both dates and timestamps) are lexicographically sortable
// because the format places the most significant unit first (year → month →
// day → hour → minute → second) and every segment is zero-padded and
// fixed-width. This means plain string comparison (< / >) produces the
// correct chronological order — no Date object or dayjs parsing needed.
//
// This matters for performance: parsing thousands of date strings with dayjs
// (which loads timezone plugins, creates wrapper objects, etc.) inside a sort
// comparator is O(n log n) parsing operations. String comparison is ~1000x
// faster for large arrays.

/**
 * Comparator function for sorting timestamps.
 * Uses lexicographic string comparison — no parsing overhead.
 * Use with Array.sort(): `timestamps.sort(compareOffsetDateTimes)`
 */
export function compareOffsetDateTimes(
  a: OffsetDateTime | undefined | null,
  b: OffsetDateTime | undefined | null,
): number {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Comparator function for sorting dates.
 * Uses lexicographic string comparison — no parsing overhead.
 * Use with Array.sort(): `dates.sort(compareLocalDates)`
 */
export function compareLocalDates(
  a: LocalDate | undefined | null,
  b: LocalDate | undefined | null,
): number {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  return a < b ? -1 : a > b ? 1 : 0;
}

// ============ Timestamp component extraction ============

/**
 * Returns the hour (0-23) from a timestamp in Vancouver timezone.
 */
export function getOffsetDateTimeHour(value: OffsetDateTime): number {
  return parseTimestamp(value).hour();
}

/**
 * Returns the minute (0-59) from a timestamp in Vancouver timezone.
 */
export function getOffsetDateTimeMinute(value: OffsetDateTime): number {
  return parseTimestamp(value).minute();
}

/**
 * Creates an OffsetDateTime from a date, hour, and minute in Vancouver timezone.
 */
export function createVancouverOffsetDateTime(
  date: LocalDate,
  hour: number,
  minute: number,
): OffsetDateTime {
  const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  return dayjs
    .tz(`${date} ${timeStr}`, VANCOUVER_TZ)
    .utc()
    .toISOString() as OffsetDateTime;
}

// ============ LocalTime helpers ============
//
// The backend stores pickup/delivery hours as plain time-of-day strings
// (e.g. "09:00:00", "17:30:00"). These helpers parse and format those
// strings without involving timezones or Date objects.

/**
 * Parse a LocalTime string ("HH:MM" or "HH:MM:SS") into hour and minute components.
 *
 * Used to extract hour/minute from API fields like `pickupLocation.hours.openFrom`.
 * Returns `{ hour: 9, minute: 0 }` when the input is undefined or empty, matching
 * the common default business-hours opening time.
 *
 * @param value - A time string in "HH:MM" or "HH:MM:SS" format, or undefined
 * @returns An object with `hour` (0-23) and `minute` (0-59)
 *
 * @example
 * parseLocalTime("09:30:00") // { hour: 9, minute: 30 }
 * parseLocalTime("17:00")    // { hour: 17, minute: 0 }
 * parseLocalTime(undefined)  // { hour: 9, minute: 0 }
 */
export function parseLocalTime(value: LocalTime | string | undefined): {
  hour: number;
  minute: number;
} {
  if (!value) return { hour: 9, minute: 0 };
  const parts = value.split(":");
  return {
    hour: Number(parts[0]) || 0,
    minute: Number(parts[1]) || 0,
  };
}

/**
 * Format hour and minute values into a LocalTime string ("HH:MM:SS").
 *
 * Produces the format expected by API fields like `pickupHours.openFrom`.
 * Both hour and minute are zero-padded to two digits, with ":00" appended
 * for the seconds component.
 *
 * @param hour - The hour (0-23)
 * @param minute - The minute (0-59)
 * @returns A LocalTime string in "HH:MM:SS" format
 *
 * @example
 * formatLocalTime(9, 0)   // "09:00:00"
 * formatLocalTime(17, 30) // "17:30:00"
 * formatLocalTime(0, 5)   // "00:05:00"
 */
export function formatLocalTime(hour: number, minute: number): LocalTime {
  return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:00` as LocalTime;
}

/**
 * Format a LocalTime value for human-readable display.
 *
 * @param value - A LocalTime string
 * @param format - Display format: "12h" (default, e.g. "9:30 AM") or "24h" (e.g. "09:30")
 * @returns Formatted time string
 *
 * @example
 * formatLocalTimeDisplay("09:30:00")         // "9:30 AM"
 * formatLocalTimeDisplay("17:00:00", "24h")  // "17:00"
 * formatLocalTimeDisplay("13:45:00", "12h")  // "1:45 PM"
 */
export function formatLocalTimeDisplay(
  value: LocalTime,
  format: "12h" | "24h" = "12h",
): string {
  const { hour, minute } = parseLocalTime(value);
  const minuteStr = minute.toString().padStart(2, "0");

  if (format === "24h") {
    return `${hour.toString().padStart(2, "0")}:${minuteStr}`;
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minuteStr} ${period}`;
}

/**
 * Parse a formatted timestamp string back into an OffsetDateTime using the format it was produced with.
 * Returns null if the input doesn't match the format or isn't valid.
 */
export function parseOffsetDateTimeWithFormat(
  text: string,
  format: string,
): OffsetDateTime | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  // Strict parse first to reject invalid values like day 89
  const strict = dayjs(trimmed, format, true);
  if (!strict.isValid()) return null;

  // Re-parse with timezone to get correct UTC conversion
  const parsed = dayjs.tz(trimmed, format, VANCOUVER_TZ);
  return parsed.utc().toISOString() as OffsetDateTime;
}

// ============ Conversion ============

/**
 * Extracts the date portion from a timestamp, returning it as a LocalDate.
 * The date is in Vancouver timezone.
 * @param value - The UTC timestamp to extract the date from
 * @returns The date portion as LocalDate (YYYY-MM-DD)
 */
export function offsetDateTimeToLocalDate(value: OffsetDateTime): LocalDate {
  return parseTimestamp(value).format("YYYY-MM-DD") as LocalDate;
}

/**
 * Converts a timestamp to milliseconds since epoch.
 * Useful for mathematical comparisons like finding min/max times.
 * @param value - The UTC timestamp to convert
 * @returns Milliseconds since Unix epoch
 */
export function offsetDateTimeToMs(value: OffsetDateTime): number {
  return parseTimestamp(value).valueOf();
}

// ============ Days calculation ============

/**
 * Returns the number of days from today until the given date.
 * Positive = future, negative = past.
 */
export function daysUntil(value: LocalDate): number {
  const target = parseDate(value);
  const todayDate = dayjs().tz(VANCOUVER_TZ).startOf("day");
  return target.diff(todayDate, "day");
}

/**
 * Returns how many days past the given date we are.
 * Returns 0 if the date is today or in the future.
 * Useful for calculating overdue days.
 * @example daysBeyond("2024-01-10") // if today is Jan 15, returns 5
 * @example daysBeyond("2024-01-20") // if today is Jan 15, returns 0
 */
export function daysBeyond(value: LocalDate | undefined | null): number {
  if (!value) return 0;
  const days = -daysUntil(value);
  return days > 0 ? days : 0;
}

/**
 * Returns true if the date is today in Vancouver timezone.
 */
export function isToday(value: LocalDate): boolean {
  return daysUntil(value) === 0;
}

/**
 * Returns how many days a range covers, counting both ends.
 *
 * Inclusive because a range picked as two calendar dates includes both of them:
 * May 18 to May 18 is one day of data, not zero. Returns 0 if the range is
 * inverted rather than a negative count.
 */
export function daysInLocalDateRange(from: LocalDate, to: LocalDate): number {
  const days = parseDate(to).diff(parseDate(from), "day") + 1;
  return days > 0 ? days : 0;
}

/**
 * Returns the number of days since the given timestamp.
 * Positive = past, negative = future.
 */
export function daysSince(value: OffsetDateTime): number {
  const target = parseTimestamp(value).startOf("day");
  const todayDate = dayjs().tz(VANCOUVER_TZ).startOf("day");
  return todayDate.diff(target, "day");
}

/**
 * Returns the number of hours since the given timestamp (as a decimal).
 * @param value - The UTC timestamp to measure from
 */
export function hoursSince(value: OffsetDateTime): number {
  const start = dayjs.utc(value);
  const now = dayjs.utc();
  return now.diff(start, "hour", true);
}

/**
 * Returns the difference in milliseconds between two timestamps.
 * @param a - The first timestamp
 * @param b - The second timestamp
 * @returns The absolute difference in milliseconds
 */
export function offsetDateTimeDiffMs(
  a: OffsetDateTime,
  b: OffsetDateTime,
): number {
  return Math.abs(dayjs.utc(a).valueOf() - dayjs.utc(b).valueOf());
}

/**
 * Returns the number of days since the given date (relative to today in Vancouver).
 * Positive = past, negative = future.
 * @param value - The date string (YYYY-MM-DD format)
 */
export function daysSinceLocalDate(
  value: LocalDate | undefined | null,
): number {
  if (!value) return 0;
  const target = dayjs.tz(value, VANCOUVER_TZ).startOf("day");
  const todayDate = dayjs().tz(VANCOUVER_TZ).startOf("day");
  return todayDate.diff(target, "day");
}

/**
 * Returns the number of weeks since the given timestamp (relative to today in Vancouver).
 * @param value - The UTC timestamp
 */
export function weeksSinceOffsetDateTime(
  value: OffsetDateTime | undefined | null,
): number {
  if (!value) return 0;
  const target = dayjs.utc(value);
  const todayDate = dayjs().tz(VANCOUVER_TZ);
  return todayDate.diff(target, "week");
}

/**
 * Returns the number of weeks since the given date (relative to today in Vancouver).
 * @param value - The date string (YYYY-MM-DD format)
 */
export function weeksSinceLocalDate(
  value: LocalDate | undefined | null,
): number {
  if (!value) return 0;
  const target = dayjs.tz(value, VANCOUVER_TZ).startOf("day");
  const todayDate = dayjs().tz(VANCOUVER_TZ).startOf("day");
  return todayDate.diff(target, "week");
}

/**
 * Returns a date relative to today by subtracting the given amount.
 * @param amount - The number of units to subtract
 * @param unit - The unit type: 'day', 'week', 'month', 'year', etc.
 * @example todayMinus(30, 'day') // 30 days ago
 * @example todayMinus(1, 'week') // 1 week ago
 * @example todayMinus(3, 'month') // 3 months ago
 */
export function todayMinus(
  amount: number,
  unit:
    | "day"
    | "days"
    | "week"
    | "weeks"
    | "month"
    | "months"
    | "year"
    | "years",
): LocalDate {
  return dayjs()
    .tz(VANCOUVER_TZ)
    .subtract(amount, unit)
    .format("YYYY-MM-DD") as LocalDate;
}

/**
 * Returns a date relative to today by adding the given amount.
 * @param amount - The number of units to add
 * @param unit - The unit type: 'day', 'week', 'month', 'year', etc.
 * @example todayPlus(1, 'day') // tomorrow
 * @example todayPlus(1, 'week') // 1 week from now
 * @example todayPlus(3, 'month') // 3 months from now
 */
export function todayPlus(
  amount: number,
  unit:
    | "day"
    | "days"
    | "week"
    | "weeks"
    | "month"
    | "months"
    | "year"
    | "years",
): LocalDate {
  return dayjs()
    .tz(VANCOUVER_TZ)
    .add(amount, unit)
    .format("YYYY-MM-DD") as LocalDate;
}

// ============ Same-or-before/after comparisons ============

/**
 * Returns true if the first date is the same as or before the second.
 */
export function isLocalDateSameOrBefore(
  value: LocalDate,
  other: LocalDate,
): boolean {
  return !isLocalDateAfter(value, other);
}

/**
 * Returns true if the first date is the same as or after the second.
 */
export function isLocalDateSameOrAfter(
  value: LocalDate,
  other: LocalDate,
): boolean {
  return !isLocalDateBefore(value, other);
}

/**
 * Returns true if the first timestamp is the same as or before the second.
 */
export function isOffsetDateTimeSameOrBefore(
  value: OffsetDateTime,
  other: OffsetDateTime,
): boolean {
  return !isOffsetDateTimeAfter(value, other);
}

// ============ Period boundaries ============

/**
 * Returns the first day of the month for the given date.
 */
export function startOfMonth(value: LocalDate): LocalDate {
  return parseDate(value).startOf("month").format("YYYY-MM-DD") as LocalDate;
}

/**
 * Returns the last day of the month for the given date.
 */
export function endOfMonth(value: LocalDate): LocalDate {
  return parseDate(value).endOf("month").format("YYYY-MM-DD") as LocalDate;
}

/**
 * Creates a LocalDate for the first day of the given month.
 * @param year - Full year (e.g. 2024)
 * @param monthIndex - 0-based month index (0 = January, 11 = December)
 */
function firstOfMonth(year: number, monthIndex: number): LocalDate {
  const m = String(monthIndex + 1).padStart(2, "0");
  return `${year}-${m}-01` as LocalDate;
}

/**
 * Creates a LocalDate for the last day of the given month.
 * @param year - Full year (e.g. 2024)
 * @param monthIndex - 0-based month index (0 = January, 11 = December)
 */
export function lastOfMonth(year: number, monthIndex: number): LocalDate {
  return endOfMonth(firstOfMonth(year, monthIndex));
}

// ============ Date arithmetic ============

/**
 * Returns the date offset by the given number of days.
 * Supports negative values to subtract days.
 */
export function addDays(value: LocalDate, days: number): LocalDate {
  return parseDate(value).add(days, "day").format("YYYY-MM-DD") as LocalDate;
}

/**
 * Returns the date offset by the given number of months.
 * Supports negative values to subtract months.
 */
export function addMonths(value: LocalDate, months: number): LocalDate {
  return parseDate(value)
    .add(months, "month")
    .format("YYYY-MM-DD") as LocalDate;
}

// ============ Business day calculations ============

/**
 * Returns the date that is N business days before the given date.
 * Business days exclude Saturday and Sunday.
 * @param value - The starting date
 * @param days - Number of business days to subtract
 */
export function subtractBusinessDays(
  value: LocalDate,
  days: number,
): LocalDate {
  let result = parseDate(value);
  let count = 0;
  while (count < days) {
    result = result.subtract(1, "day");
    const dayOfWeek = result.day();
    // Skip Saturday (6) and Sunday (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
  }
  return result.format("YYYY-MM-DD") as LocalDate;
}

// ============ Weekend / Week utilities ============

/**
 * Returns true if the date falls on a weekend (Saturday or Sunday).
 */
export function isWeekend(value: LocalDate): boolean {
  const dayOfWeek = parseDate(value).isoWeekday();
  return dayOfWeek === 6 || dayOfWeek === 7;
}

/**
 * Returns the ISO week number (1-52/53) for the given date.
 */
export function getWeekOfYear(value: LocalDate): number {
  return parseDate(value).week();
}

// ============ JS Date utilities (for third-party library callbacks like nivo) ============

/**
 * Converts a JS Date to LocalDate.
 * Extracts date components directly to avoid timezone shifts.
 * Use this for third-party library callbacks that give you JS Date objects.
 */
export function fromJsDate(date: Date): LocalDate {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}` as LocalDate;
}

/**
 * Splits a LocalDate into its numeric year/month/day parts, failing fast if the
 * value is not the branded YYYY-MM-DD shape (which callers rely on downstream).
 */
function localDateParts(
  value: LocalDate,
): [year: number, month: number, day: number] {
  const [year, month, day] = value.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Invalid LocalDate: ${value}`);
  }
  return [year, month, day];
}

/**
 * Converts a LocalDate to JS Date.
 * Parses date components directly to avoid timezone shifts.
 * Use this for third-party libraries that require JS Date objects.
 */
export function toJsDate(value: LocalDate): Date {
  const [year, month, day] = localDateParts(value);
  return new Date(year, month - 1, day);
}

const FULL_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** Full month name for a 1-indexed month (1 = January). */
export function monthName(month: number): string {
  const name = FULL_MONTH_NAMES[month - 1];
  if (name === undefined) {
    throw new Error(`Invalid month: ${month}`);
  }
  return name;
}

/**
 * Number of days in a 1-indexed month, leap-safe (February returns 29) so a
 * year-less month/day picker never rejects Feb 29.
 */
export function daysInMonth(month: number): number {
  return new Date(2000, month, 0).getDate();
}

/**
 * Formats a year-less month + day for display (e.g. "May 12"). Both are 1-indexed
 * (month 1 = January). Used for birthdays, where only the day and month matter.
 */
export function formatMonthDay(month: number, day: number): string {
  return `${monthName(month)} ${day}`;
}

/**
 * Extract year, month (1-12), and day components from a LocalDate.
 * Useful for passing to holiday functions that take separate year/month/day parameters.
 */
export function getLocalDateComponents(value: LocalDate): {
  year: number;
  month: number;
  day: number;
} {
  const [year, month, day] = localDateParts(value);
  return { year, month, day };
}

const MAX_BUSINESS_DAY_SEARCH = 14;

/**
 * The first business day on or after `from`, skipping weekends and optionally
 * holidays. Returns `from` unchanged when it already is a business day.
 *
 * Prefer this over `getNextBusinessDay` whenever a date is already in hand —
 * anchoring on today would move a date the user picked in the future *backwards*.
 *
 * Looks forward up to 14 days, then gives up and returns `from`.
 *
 * @param isHolidayFn - Optional check for a holiday. Holidays are usually
 *                      regional, so the caller supplies the right calendar.
 */
export function firstBusinessDayFrom(
  from: LocalDate,
  isHolidayFn?: (year: number, month: number, day: number) => boolean,
): LocalDate {
  for (let i = 0; i <= MAX_BUSINESS_DAY_SEARCH; i++) {
    const candidate = addDays(from, i);
    const { year, month, day } = getLocalDateComponents(candidate);
    const isHolidayDay = isHolidayFn ? isHolidayFn(year, month, day) : false;

    if (!isWeekend(candidate) && !isHolidayDay) {
      return candidate;
    }
  }

  return from;
}

/**
 * Get the next business day (skipping weekends and optionally holidays).
 * Starts from today and looks forward up to 14 days.
 * If no business day is found within 14 days, returns today.
 *
 * Note this deliberately does NOT delegate to `firstBusinessDayFrom`: the two
 * differ on exhaustion (this returns today, that returns its start date) and on
 * inclusivity (this is strictly after today).
 *
 * @param isHolidayFn - Optional function to check if a date is a holiday.
 *                      If not provided, only weekends are skipped.
 */
export function getNextBusinessDay(
  isHolidayFn?: (year: number, month: number, day: number) => boolean,
): LocalDate {
  const todayDate = toJsDate(today());

  for (let i = 1; i <= MAX_BUSINESS_DAY_SEARCH; i++) {
    const current = new Date(todayDate);
    current.setDate(todayDate.getDate() + i);
    const dateString = fromJsDate(current);
    const { year, month, day } = getLocalDateComponents(dateString);
    const isHolidayDay = isHolidayFn ? isHolidayFn(year, month, day) : false;

    if (!isWeekend(dateString) && !isHolidayDay) {
      return dateString;
    }
  }

  return today();
}

/**
 * Parse a formatted date string back into a LocalDate using the format it was produced with.
 * This is the inverse of formatLocalDate — pass the same format string used to format the date.
 * Returns null if the input doesn't match the format or isn't a valid date.
 *
 * @param text - The formatted date string (e.g. "Jan 15, 2025")
 * @param format - The dayjs format string used to produce it (e.g. "MMM D, YYYY")
 */
export function parseLocalDateWithFormat(
  text: string,
  format: string,
): LocalDate | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const parsed = dayjs(trimmed, format, true);
  if (!parsed.isValid()) return null;

  return parsed.format("YYYY-MM-DD") as LocalDate;
}
